/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import * as qs from 'query-string';
import { defaultTo, identity } from 'lodash';
import { FORM_ERROR } from 'final-form';
import { getConfig } from 'blb-utils/config';
import { errorMessage } from 'blb-utils/errors';
import { getMe } from 'blb-api/auth';
import { cookies, getSignedJSONCookie } from './cookies';
import { isForSecondaryAuthQueryParam, isPrimarySessionSet, isSecondaryAuthEnabled, isSecondarySessionSet, isUsingSecondarySession, useSecondarySession } from 'blb-utils/secondaryAuth';
const failedCookieExtraction = {
  cookie: null,
  type: 'failure'
};

// once user submits a "task" in the login flow and the BE responds with a cookie saying "okay well based off this task, here is the next task you should be in"
// this essentially acts as an ephemeral bookmark/validator of sorts so if the flow is broken the user can reenter the app and pick up where they left off in the flow
export const useChallengeCookie = () => {
  const {
    challengeCookieName
  } = getConfig();
  const challengeCookieNotExtracted = cookies.get(challengeCookieName);
  if (!challengeCookieNotExtracted || typeof challengeCookieNotExtracted !== 'string') return failedCookieExtraction;
  try {
    const parsedCookie = getSignedJSONCookie(challengeCookieNotExtracted);
    return {
      cookie: parsedCookie,
      type: 'success'
    };
  } catch (error) {
    Sentry.captureException(error);
    return failedCookieExtraction;
  }
};
export const useCancelHook = () => {
  const history = useHistory();
  const {
    challengeCookieName
  } = getConfig();
  return () => {
    cookies.remove(challengeCookieName);
    history.push('/login');
  };
};
export const useLoginFlowSubmit = function (submitAPI, setMe) {
  let formValuesTransformer = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : identity;
  const defaultError = errorMessage('LOGGING_IN');
  const history = useHistory();
  const onSubmit = async values => {
    try {
      const {
        data
      } = await submitAPI(formValuesTransformer(values));
      if (data.resultType === 'success') {
        if (isSecondaryAuthEnabled() && isPrimarySessionSet() && !isSecondarySessionSet() && !isUsingSecondarySession()) {
          // primary auth is set and this is logging into secondary auth
          // Doing this so the getMe() below can fetch secondary session user
          useSecondarySession();
        }
        const {
          data: me
        } = await getMe();
        setMe(me);
        // Go to app root to prevent hitting /login routes again. For more details:
        // https://paper.dropbox.com/doc/Eng-spec-Workspace-Identity-Consolidation--BQsjxtOvC2f7PE9chx3qRbSSAg-1dpwhD8lUP5ZjHKmPzEuC
        history.push('/');
        return;
      }
      const newParams = new URLSearchParams();
      const forSecondaryAuth = isForSecondaryAuthQueryParam();
      if (forSecondaryAuth) {
        newParams.append('forSecondary', forSecondaryAuth);
      }
      history.push({
        pathname: data.location,
        search: newParams.toString()
      });
    } catch (err) {
      const responseError = err.response?.data.error;

      // if the error is NOT actionable just surface a failed request in the UI
      if (!responseError?.type) {
        return {
          [FORM_ERROR]: defaultError
        };
      }
      const {
        generalError,
        inputErrors,
        type
      } = responseError;
      switch (type) {
        case 'redirectToStart':
          {
            return history.push({
              pathname: '/login',
              search: qs.stringify({
                error: defaultTo(generalError, defaultError)
              })
            });
          }
        case 'secondaryAuthNotSupportedError':
        case 'submitError':
          {
            return {
              [FORM_ERROR]: generalError,
              ...inputErrors
            };
          }
      }
    }
  };
  return onSubmit;
};
export const useQuery = () => {
  return qs.parse(useLocation().search);
};
export const extractValue = param => {
  const argStart = param.indexOf('=');
  return param.slice(argStart + 1);
};
export const principalTypeDisplayName = {
  attorney: 'Attorney',
  insurance_qc_agent: 'Insurance QC Agent',
  loan_processor: 'Loan Processor',
  multi_tenant_tpr: 'Multi-tenant TPR',
  notary: 'Notary',
  ops_desk_agent: 'Ops Desk Agent',
  settlement_agent: 'Settlement Agent',
  title_agent: 'Title Agent'
};